import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Segmented, Space, Table, Tag, Typography } from 'antd';
import nhsvRightsHistoryApiCall, {
  NHSV_RIGHT_TYPES,
  NHSVRightType,
} from 'apps/nhsv/apiCalls/nhsv/rights-history';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const { Title, Text } = Typography;

const formatNumber = (value: string | undefined) => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toLocaleString('vi-VN');
};

// NHSV dates are yyyyMMdd. "00000000" (or "00/00/0000" in NHSV's doc) means
// no date, and upcoming rights use "21000101" for a stock-in date not set yet.
const formatDate = (value: string | undefined) => {
  if (!value || /^[0/]+$/.test(value) || value === '21000101') return '-';
  const m = moment(value, 'YYYYMMDD', true);
  return m.isValid() ? m.format('DD/MM/YYYY') : value;
};

const formatYesNo = (value: string | undefined) => {
  if (value === 'Y') return <Tag color="green">Y</Tag>;
  if (value === 'N') return <Tag>N</Tag>;
  return value || '-';
};

const number = (title: string, dataIndex: string) => ({
  title, dataIndex, key: dataIndex, render: formatNumber, align: 'right' as const, width: 100,
});
const date = (title: string, dataIndex: string) => ({
  title, dataIndex, key: dataIndex, render: formatDate, width: 110,
});
const yesNo = (title: string, dataIndex: string) => ({
  title, dataIndex, key: dataIndex, render: formatYesNo, width: 90,
});

const commonColumns = [
  { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 90, fixed: 'left' as const },
  { title: 'Seq', dataIndex: 'seq', key: 'seq', width: 60 },
  date('Record Date', 'base_date'),
  { title: 'Status', dataIndex: 'status', key: 'status', width: 220 },
  { title: 'Rate', key: 'rate', width: 90, render: (_: unknown, r: any) => `${r.base_rate || '-'} : ${r.divd_rate || '-'}` },
  number('Owned Qty', 'own_qtty'),
  number('Dividend Qty', 'avail_qtty'),
  date('Effect Date', 'effect_date'),
  yesNo('Shares In', 'effect_yn'),
];

const oddLotColumns = [
  number('Odd-lot Price', 'flotq_std_pri'),
  number('Odd-lot Cash', 'flotq_amt'),
  date('Odd-lot Pay Date', 'flotq_pay_dt'),
];

const columnsByType: Partial<Record<NHSVRightType, any[]>> = {
  'bonus-share': [...commonColumns, ...oddLotColumns],
  dividend: [
    ...commonColumns,
    number('Tax %', 'tax_rate'),
    number('Cash (after tax)', 'asn_amt'),
    date('Cash Pay Date', 'divi_pay_dt'),
    yesNo('Cash Paid', 'rcpt_trd_no_yn'),
    ...oddLotColumns,
  ],
  upcoming: [
    { title: 'Symbol', dataIndex: 'stk_cd', key: 'stk_cd', width: 90, fixed: 'left' as const },
    { title: 'Right Type', dataIndex: 'rgt_tp', key: 'rgt_tp', width: 160 },
    date('Record Date', 'rgt_std_dt'),
    number('Quantity', 'qty'),
    date('Shares In Date', 'inq_dt'),
    number('Cash Dividend', 'asn_amt'),
    date('Cash Pay Date', 'divi_pay_dt'),
    number('Odd-lot Cash', 'flotq_amt'),
    date('Odd-lot Pay Date', 'flotq_dt'),
  ],
};

// Types without hand-picked columns show every field NHSV returned, in order.
const rawColumns = (items: any[]) => {
  const keys: string[] = [];
  items.forEach(item =>
    Object.keys(item || {}).forEach(key => {
      if (!keys.includes(key)) keys.push(key);
    })
  );
  return keys.map(key => ({
    title: key,
    dataIndex: key,
    key,
    width: 120,
    render: (value: unknown) => (value === null || value === undefined || value === '' ? '-' : String(value)),
  }));
};

const rowKeyByType: Partial<Record<NHSVRightType, (record: any) => string>> = {
  'bonus-share': record => `${record.symbol}_${record.base_date}_${record.seq}`,
  dividend: record => `${record.symbol}_${record.base_date}_${record.seq}`,
  upcoming: record => record.next_key || `${record.stk_cd}_${record.rgt_std_dt}_${record.rgt_tp}`,
};

const typeOptions: { label: string; value: NHSVRightType }[] = [
  { label: 'Upcoming (010)', value: 'upcoming' },
  { label: 'Dividend (013)', value: 'dividend' },
  { label: 'Bonus share (012)', value: 'bonus-share' },
  { label: 'Issue (011)', value: 'issue' },
  { label: 'Conversion (015)', value: 'conversion' },
  { label: 'Other (017)', value: 'other' },
  { label: 'Bond (014)', value: 'bond' },
  { label: 'Bond interest (016)', value: 'bond-interest' },
];

const isRightType = (value: string | null): value is NHSVRightType =>
  NHSV_RIGHT_TYPES.includes(value as NHSVRightType);

export default function NHSVRightsHistoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlAccountNumber = searchParams.get('account_number') || '';
  const urlType = searchParams.get('type');

  const [inputValue, setInputValue] = useState(urlAccountNumber);
  const [rightType, setRightType] = useState<NHSVRightType>(isRightType(urlType) ? urlType : 'dividend');
  const [searchedType, setSearchedType] = useState<NHSVRightType>(rightType);

  const { data, load, isLoading } = useFetchApiGet(nhsvRightsHistoryApiCall, {
    resourceName: 'data',
  });

  const search = (accountNumber: string, type: NHSVRightType) => {
    const trimmed = accountNumber.trim();
    if (!trimmed) return;
    setInputValue(trimmed);
    setSearchedType(type);
    setSearchParams({ account_number: trimmed, type });
    load({ account_number: trimmed, type });
  };

  useEffect(() => {
    if (urlAccountNumber) search(urlAccountNumber, rightType);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const items: any[] = Array.isArray(data) ? data : [];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>NHSV Rights History</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Raw rights from NHSV: upcoming (ASSET-010) are not credited yet; dividend (ASSET-013) and
        bonus share (ASSET-012) feed the daily stock dividend sync; the others (ASSET-011, 014-017)
        are lookup only.
      </Text>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space wrap>
          <Input
            placeholder="Enter trading account number"
            size="large"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={() => search(inputValue, rightType)}
            style={{ width: 300 }}
            allowClear
          />
          <Segmented
            size="large"
            value={rightType}
            onChange={value => {
              const type = value as NHSVRightType;
              setRightType(type);
              if (inputValue.trim()) search(inputValue, type);
            }}
            options={typeOptions}
          />
          <Button
            type="primary"
            size="large"
            icon={<SearchOutlined />}
            onClick={() => search(inputValue, rightType)}
            loading={isLoading}
          >
            Lookup
          </Button>
        </Space>

        {searchParams.get('account_number') && (
          <Table
            dataSource={items}
            columns={columnsByType[searchedType] || rawColumns(items)}
            rowKey={rowKeyByType[searchedType] || ((record: any) => JSON.stringify(record))}
            pagination={{ pageSize: 50, hideOnSinglePage: true }}
            size="small"
            scroll={{ x: 1600 }}
            locale={{ emptyText: 'No rights found' }}
            loading={isLoading}
          />
        )}
      </Space>
    </div>
  );
}
